import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { adalApiFetch } from '../../../adalConfig';
import { getAPIDataReturn } from '../../utils/apiFunction';

const CustomerRatesTable = React.memo(({
    filteredSortedCustomers,
    fullAddress,
    customerDraftGroups,
    groups,
    updateCustomerGroup,
    customerDraftEnabled,
    updateCustomerEnabled,
    customerDraftRates,
    updateCustomerRate,
    saveCustomer,
    isCustomerDirty,
}) => {
    const ROW_HEIGHT = 72;
    const OVERSCAN = 10;
    const containerRef = React.useRef(null);
    const [scrollTop, setScrollTop] = React.useState(0);
    const [viewportHeight, setViewportHeight] = React.useState(560);

    React.useEffect(() => {
        const updateViewportHeight = () => {
            if (containerRef.current) {
                setViewportHeight(containerRef.current.clientHeight || 560);
            }
        };

        updateViewportHeight();
        window.addEventListener('resize', updateViewportHeight);
        return () => window.removeEventListener('resize', updateViewportHeight);
    }, []);

    const totalRows = filteredSortedCustomers.length;
    const startIndex = Math.max(Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN, 0);
    const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
    const endIndex = Math.min(startIndex + visibleCount, totalRows);
    const visibleRows = React.useMemo(
        () => filteredSortedCustomers.slice(startIndex, endIndex),
        [filteredSortedCustomers, startIndex, endIndex]
    );
    const topSpacerHeight = startIndex * ROW_HEIGHT;
    const bottomSpacerHeight = Math.max((totalRows - endIndex) * ROW_HEIGHT, 0);
    const groupRateById = React.useMemo(() => {
        const map = {};
        groups.forEach((group) => {
            map[group.id] = group.rate;
        });
        return map;
    }, [groups]);

    return (
        <Box
            ref={containerRef}
            sx={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'auto' }}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell width={140}>Rates Enabled</TableCell>
                        <TableCell width={220}>Group</TableCell>
                        <TableCell width={160}>Rate ($/doz)</TableCell>
                        <TableCell width={120}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {topSpacerHeight > 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, border: 0, height: `${topSpacerHeight}px` }} />
                        </TableRow>
                    ) : null}

                    {visibleRows.map((customer) => (
                        (() => {
                            const selectedGroupId = customerDraftGroups[customer.customerId] ?? '';
                            const hasSelectedGroup = selectedGroupId !== '' && selectedGroupId !== null && selectedGroupId !== undefined;
                            const inputRate = hasSelectedGroup
                                ? (groupRateById[selectedGroupId] ?? '')
                                : (customerDraftRates[customer.customerId] ?? '');

                            return (
                        <TableRow
                            key={customer.customerId}
                            sx={{
                                height: `${ROW_HEIGHT}px`,
                                ...(customer.hasRate ? {} : { backgroundColor: '#fff1f0' }),
                            }}
                        >
                            <TableCell>{customer.customerName}</TableCell>
                            <TableCell>{fullAddress(customer)}</TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={customerDraftEnabled[customer.customerId] ?? false}
                                    onChange={(e) => updateCustomerEnabled(customer.customerId, e.target.checked)}
                                />
                            </TableCell>
                            <TableCell>
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={customerDraftGroups[customer.customerId] ?? ''}
                                        onChange={(e) => updateCustomerGroup(customer.customerId, e.target.value)}
                                    >
                                        <MenuItem value="">No Group</MenuItem>
                                        {groups.map((group) => (
                                            <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ step: '0.01' }}
                                    value={inputRate}
                                    onChange={(e) => updateCustomerRate(customer.customerId, e.target.value)}
                                    disabled={hasSelectedGroup || !(customerDraftEnabled[customer.customerId] ?? false)}
                                    sx={!customer.hasRate ? { backgroundColor: '#ffe6e6', borderRadius: 1 } : undefined}
                                />
                                {hasSelectedGroup ? (
                                    <Typography variant="caption" color="text.secondary">Rate is controlled by selected group</Typography>
                                ) : null}
                                {!(customerDraftEnabled[customer.customerId] ?? false) ? (
                                    <Typography variant="caption" color="text.secondary">Rates disabled for this customer</Typography>
                                ) : null}
                                {!customer.hasRate && !hasSelectedGroup ? (
                                    <Typography variant="caption" color="error">No rate record</Typography>
                                ) : null}
                            </TableCell>
                            <TableCell>
                                {isCustomerDirty(customer) ? (
                                    <Button variant="outlined" onClick={() => saveCustomer(customer)}>Save</Button>
                                ) : null}
                            </TableCell>
                        </TableRow>
                            );
                        })()
                    ))}

                    {bottomSpacerHeight > 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, border: 0, height: `${bottomSpacerHeight}px` }} />
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </Box>
    );
});

const FuelSurchargeRates = () => {
    const uri = process.env.REACT_APP_BASE_URL;
    const [loading, setLoading] = React.useState(false);
    const [groups, setGroups] = React.useState([]);
    const [initialGroupsById, setInitialGroupsById] = React.useState({});
    const [customers, setCustomers] = React.useState([]);
    const [customerDraftEnabled, setCustomerDraftEnabled] = React.useState({});
    const [customerDraftRates, setCustomerDraftRates] = React.useState({});
    const [customerDraftGroups, setCustomerDraftGroups] = React.useState({});
    const [customerSearch, setCustomerSearch] = React.useState('');
    const [debouncedCustomerSearch, setDebouncedCustomerSearch] = React.useState('');
    const [rateSort, setRateSort] = React.useState('rate-asc');
    const [newGroupName, setNewGroupName] = React.useState('');
    const [newGroupRate, setNewGroupRate] = React.useState('');

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCustomerSearch(customerSearch);
        }, 180);

        return () => clearTimeout(timer);
    }, [customerSearch]);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            const [groupData, customerResponse] = await Promise.all([
                getAPIDataReturn('/FuelSurcharge/Groups/Get'),
                adalApiFetch(fetch, uri + '/FuelSurcharge/Customers/Get'),
            ]);
            const customerData = await customerResponse.json();
            const groupRateLookup = {};
            (groupData || []).forEach((g) => {
                groupRateLookup[g.id] = Number(g.rate ?? 0);
            });

            setGroups(groupData || []);
            const nextInitialGroupsById = {};
            (groupData || []).forEach((group) => {
                nextInitialGroupsById[group.id] = {
                    name: group.name ?? '',
                    rate: Number(group.rate ?? 0),
                };
            });
            setInitialGroupsById(nextInitialGroupsById);

            const mappedCustomers = (customerData || []).map((x) => ({
                ...x,
                hasRate: x.rate !== null && x.rate !== undefined,
                originalRate: Number(x.rate ?? 0),
                originalGroupId: x.groupId ?? '',
                // Keep a stable sort snapshot so edits/saves don't jump rows on screen.
                sortRate: x.groupId ? Number(groupRateLookup[x.groupId] ?? 0) : Number(x.rate ?? 0),
            }));

            const nextRates = {};
            const nextGroups = {};
            const nextEnabled = {};
            mappedCustomers.forEach((x) => {
                nextRates[x.customerId] = x.rate ?? '';
                nextGroups[x.customerId] = x.groupId ?? '';
                nextEnabled[x.customerId] = x.ratesEnabled ?? false;
            });

            setCustomers(mappedCustomers);
            setCustomerDraftRates(nextRates);
            setCustomerDraftGroups(nextGroups);
            setCustomerDraftEnabled(nextEnabled);
        } finally {
            setLoading(false);
        }
    }, [uri]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const postWithParams = React.useCallback(async (path, params) => {
        await adalApiFetch(fetch, uri + path + '?' + new URLSearchParams(params), {
            method: 'post',
        });
    }, [uri]);

    const updateCustomerRate = React.useCallback((customerId, value) => {
        setCustomerDraftRates((prev) => ({
            ...prev,
            [customerId]: value,
        }));
    }, []);

    const updateCustomerGroup = React.useCallback((customerId, value) => {
        setCustomerDraftGroups((prev) => ({
            ...prev,
            [customerId]: value,
        }));
    }, []);

    const updateCustomerEnabled = React.useCallback((customerId, value) => {
        setCustomerDraftEnabled((prev) => ({
            ...prev,
            [customerId]: value,
        }));
    }, []);

    const groupRateById = React.useMemo(() => {
        const map = {};
        groups.forEach((group) => {
            map[group.id] = Number(group.rate ?? 0);
        });
        return map;
    }, [groups]);

    const saveCustomer = React.useCallback(async (customer) => {
        const draftRate = customerDraftRates[customer.customerId];
        const draftGroupId = customerDraftGroups[customer.customerId];
        const ratesEnabled = customerDraftEnabled[customer.customerId] ?? false;
        const hasSelectedGroup = draftGroupId !== '' && draftGroupId !== null && draftGroupId !== undefined;
        const savedRate = hasSelectedGroup ? Number(groupRateById[draftGroupId] ?? 0) : Number(draftRate || 0);

        const params = {
            CustomerId: customer.customerId,
            Rate: savedRate,
            RatesEnabled: ratesEnabled,
        };

        if (draftGroupId !== '' && draftGroupId !== null && draftGroupId !== undefined) {
            params.FuelSurchargeGroupId = draftGroupId;
        }

        await postWithParams('/FuelSurcharge/Customers/Post', params);

        // Update local originals so row stays clean without resorting/reloading.
        setCustomers((prev) => prev.map((x) => {
            if (x.customerId !== customer.customerId) {
                return x;
            }

            return {
                ...x,
                hasRate: true,
                ratesEnabled: ratesEnabled,
                originalRate: savedRate,
                originalGroupId: hasSelectedGroup ? draftGroupId : '',
                sortRate: x.sortRate,
            };
        }));
    }, [customerDraftEnabled, customerDraftGroups, customerDraftRates, groupRateById, postWithParams]);

    const createGroup = async () => {
        if (!newGroupName.trim()) {
            return;
        }

        await postWithParams('/FuelSurcharge/Groups/Post', {
            Name: newGroupName.trim(),
            Rate: Number(newGroupRate || 0),
        });

        setNewGroupName('');
        setNewGroupRate('');
        await loadData();
    };

    const saveGroup = async (group) => {
        await postWithParams('/FuelSurcharge/Groups/Post', {
            Id: group.id,
            Name: group.name,
            Rate: Number(group.rate || 0),
        });

        // Mark group row as clean without reloading/screen jump.
        setInitialGroupsById((prev) => ({
            ...prev,
            [group.id]: {
                name: group.name ?? '',
                rate: Number(group.rate ?? 0),
            },
        }));
    };

    const updateGroupField = (groupId, field, value) => {
        setGroups((prev) => prev.map((x) => (
            x.id === groupId ? { ...x, [field]: value } : x
        )));
    };

    const isGroupDirty = React.useCallback((group) => {
        const original = initialGroupsById[group.id];
        if (!original) {
            return true;
        }

        const currentName = (group.name ?? '').trim();
        const originalName = (original.name ?? '').trim();
        const currentRate = Number(group.rate ?? 0);
        const originalRate = Number(original.rate ?? 0);

        return currentName !== originalName || currentRate !== originalRate;
    }, [initialGroupsById]);

    const setAllRatesEnabled = React.useCallback(async (enabled) => {
        await postWithParams('/FuelSurcharge/Customers/SetEnabledAll', {
            Enabled: enabled,
        });

        setCustomerDraftEnabled((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((key) => {
                updated[key] = enabled;
            });
            return updated;
        });

        setCustomers((prev) => prev.map((x) => ({
            ...x,
            ratesEnabled: enabled,
        })));
    }, [postWithParams]);

    const fullAddress = React.useCallback((customer) => {
        return [customer.addressLine1, customer.addressLine2, customer.city, customer.state, customer.postcode]
            .filter((x) => !!x)
            .join(', ');
    }, []);

    const groupNameById = React.useMemo(() => {
        const map = {};
        groups.forEach((group) => {
            map[group.id] = group.name;
        });
        return map;
    }, [groups]);

    const getGroupName = React.useCallback((customer) => {
        const draftGroupId = customerDraftGroups[customer.customerId];
        if (draftGroupId !== '' && draftGroupId !== null && draftGroupId !== undefined && groupNameById[draftGroupId]) {
            return groupNameById[draftGroupId];
        }
        return customer.groupName || '';
    }, [customerDraftGroups, groupNameById]);

    const isCustomerDirty = React.useCallback((customer) => {
        const draftGroupId = customerDraftGroups[customer.customerId] ?? '';
        const originalGroupId = customer.originalGroupId ?? '';
        const draftEnabled = customerDraftEnabled[customer.customerId] ?? false;
        const originalEnabled = customer.ratesEnabled ?? false;

        if (draftEnabled !== originalEnabled) {
            return true;
        }


        // Group selected: row cleanliness is based on group assignment only.
        if (draftGroupId !== originalGroupId) {
            return true;
        }

        if (draftGroupId !== '') {
            return false;
        }

        const draftRate = Number(customerDraftRates[customer.customerId] ?? 0);
        const originalRate = Number(customer.originalRate ?? 0);
        return draftRate !== originalRate;
    }, [customerDraftEnabled, customerDraftGroups, customerDraftRates]);

    const filteredSortedCustomers = React.useMemo(() => {
        const search = debouncedCustomerSearch.trim().toLowerCase();
        let list = [...customers];

        if (search) {
            list = list.filter((customer) => {
                const text = [
                    customer.customerName,
                    fullAddress(customer),
                    getGroupName(customer),
                ]
                    .filter((x) => !!x)
                    .join(' ')
                    .toLowerCase();

                return text.includes(search);
            });
        }

        list.sort((a, b) => {
            const rateA = Number(a.sortRate ?? 0);
            const rateB = Number(b.sortRate ?? 0);
            const stateA = (a.state || '').toLowerCase();
            const stateB = (b.state || '').toLowerCase();
            const nameA = (a.customerName || '').toLowerCase();
            const nameB = (b.customerName || '').toLowerCase();

            if (rateSort === 'state-desc') {
                const stateCompare = stateB.localeCompare(stateA);
                return stateCompare === 0 ? nameA.localeCompare(nameB) : stateCompare;
            }

            if (rateSort === 'state-asc') {
                const stateCompare = stateA.localeCompare(stateB);
                return stateCompare === 0 ? nameA.localeCompare(nameB) : stateCompare;
            }

            if (rateSort === 'rate-desc') {
                if (rateB !== rateA) {
                    return rateB - rateA;
                }
                return nameA.localeCompare(nameB);
            }

            if (rateA !== rateB) {
                return rateA - rateB;
            }
            return nameA.localeCompare(nameB);
        });

        return list;
    }, [debouncedCustomerSearch, customers, rateSort, getGroupName, fullAddress]);

    const ratesStatus = React.useMemo(() => {
        const values = Object.values(customerDraftEnabled);
        if (values.length === 0) {
            return { allEnabled: false, message: 'No customer rate records loaded.' };
        }

        const enabledCount = values.filter((x) => x === true).length;
        const allEnabled = enabledCount === values.length;

        if (allEnabled) {
            return { allEnabled: true, message: 'Rates are enabled.' };
        }

        return {
            allEnabled: false,
            message: `(${enabledCount}/${values.length}) rates enabled.`,
        };
    }, [customerDraftEnabled]);

    return (
        <Grid container spacing={2.5}>
            <Grid item xs={12}>
                <Typography variant="h2">Fuel Surcharge Management</Typography>
            </Grid>

            <Grid item xs={12}>
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: ratesStatus.allEnabled ? '#4caf50' : '#ff9800',
                        backgroundColor: ratesStatus.allEnabled ? '#e8f5e9' : '#fff3e0',
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ratesStatus.message}
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                            <TextField
                                label="New Group Name"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                size="small"
                            />
                            <TextField
                                label="Rate ($/doz)"
                                type="number"
                                value={newGroupRate}
                                onChange={(e) => setNewGroupRate(e.target.value)}
                                size="small"
                                inputProps={{ step: '0.01' }}
                            />
                            <Button variant="contained" onClick={createGroup}>Create Group</Button>
                        </Stack>

                        <Box mt={2}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Group Name</TableCell>
                                        <TableCell>Rate ($/doz)</TableCell>
                                        <TableCell width={120}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groups.map((group) => (
                                        <TableRow key={group.id}>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={group.name}
                                                    onChange={(e) => updateGroupField(group.id, 'name', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    inputProps={{ step: '0.01' }}
                                                    value={group.rate}
                                                    onChange={(e) => updateGroupField(group.id, 'rate', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {isGroupDirty(group) ? (
                                                    <Button variant="outlined" onClick={() => saveGroup(group)}>Save</Button>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Button variant="outlined" onClick={() => setAllRatesEnabled(true)}>Enable All</Button>
                            <Button variant="outlined" onClick={() => setAllRatesEnabled(false)}>Disable All</Button>
                            <TextField
                                size="small"
                                label="Search Customers"
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                sx={{ minWidth: 260 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 240 }}>
                                <InputLabel id="fuel-rate-sort-label">Sort Customers</InputLabel>
                                <Select
                                    labelId="fuel-rate-sort-label"
                                    value={rateSort}
                                    label="Sort Customers"
                                    onChange={(e) => setRateSort(e.target.value)}
                                >
                                    <MenuItem value="rate-asc">Rate Low-High</MenuItem>
                                    <MenuItem value="rate-desc">Rate High-Low</MenuItem>
                                    <MenuItem value="state-asc">State A-Z</MenuItem>
                                    <MenuItem value="state-desc">State Z-A</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <Box mt={2}>
                            <CustomerRatesTable
                                filteredSortedCustomers={filteredSortedCustomers}
                                fullAddress={fullAddress}
                                customerDraftGroups={customerDraftGroups}
                                groups={groups}
                                updateCustomerGroup={updateCustomerGroup}
                                customerDraftEnabled={customerDraftEnabled}
                                updateCustomerEnabled={updateCustomerEnabled}
                                customerDraftRates={customerDraftRates}
                                updateCustomerRate={updateCustomerRate}
                                saveCustomer={saveCustomer}
                                isCustomerDirty={isCustomerDirty}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {loading ? (
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                </Grid>
            ) : null}
        </Grid>
    );
};

export default FuelSurchargeRates;
